import posthog, { type PostHogConfig } from "posthog-js"

export type PublicAnalyticsConfig = {
  key: string
  host: string
}

export type AnalyticsEventName =
  | "website_review_form_viewed"
  | "website_review_submitted"
  | "consultation_form_viewed"
  | "consultation_requested"
  | "case_study_viewed"
  | "language_changed"

type AnalyticsProperties = {
  locale?: "hr" | "en"
  page_id?: string
  lead_kind?: "website_review" | "consultation"
}

type AnalyticsClient = {
  init: (key: string, config: Partial<PostHogConfig>) => unknown
  capture: (event: string, properties?: Record<string, unknown>) => unknown
  get_distinct_id: () => string
  has_opted_out_capturing?: () => boolean
  opt_in_capturing?: (options?: { captureEventName?: string | null | false }) => unknown
  opt_out_capturing?: () => unknown
  set_config?: (config: Partial<PostHogConfig>) => unknown
  stopSessionRecording?: () => unknown
}

type AnalyticsPage = {
  component: string
  props: object
}

type AnalyticsEvent = {
  event: string
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
  [key: string]: unknown
}

const EVENT_PROPERTIES: Record<AnalyticsEventName, Array<keyof AnalyticsProperties>> = {
  website_review_form_viewed: ["locale", "page_id", "lead_kind"],
  website_review_submitted: ["locale", "page_id", "lead_kind"],
  consultation_form_viewed: ["locale", "page_id", "lead_kind"],
  consultation_requested: ["locale", "page_id", "lead_kind"],
  case_study_viewed: ["locale", "page_id"],
  language_changed: ["locale", "page_id"],
}

export const APPROVED_ANALYTICS_EVENTS = Object.freeze(
  Object.keys(EVENT_PROPERTIES) as AnalyticsEventName[],
)

const PUBLIC_PAGE_IDS = new Set([
  "home",
  "work",
  "cajanka",
  "vancleef",
  "services",
  "about",
  "website_review",
  "website_review_thank_you",
  "contact",
  "contact_thank_you",
  "privacy",
])

const FORBIDDEN_PROPERTY_NAMES = new Set([
  "email",
  "name",
  "company",
  "company_name",
  "contact_name",
  "website_url",
  "message",
  "form_values",
  "value",
])

let activeClient: AnalyticsClient | null = null
let analyticsEnabled = false

declare global {
  interface Window {
    posthog?: AnalyticsClient
  }
}

export function initializePublicAnalytics(
  config: PublicAnalyticsConfig | null | undefined,
  client: AnalyticsClient = posthog,
) {
  if (!validConfig(config)) return false

  try {
    if (!activeClient) {
      client.init(config.key, privacyFirstConfig(config.host))
      activeClient = client
      if (activeClient.has_opted_out_capturing?.()) {
        activeClient.opt_in_capturing?.({ captureEventName: false })
      }
    } else {
      activeClient.set_config?.(privacyFirstConfig(config.host))
      activeClient.opt_in_capturing?.({ captureEventName: false })
    }
    analyticsEnabled = true
    window.posthog = activeClient
    return true
  } catch {
    analyticsEnabled = false
    return false
  }
}

export function disablePublicAnalytics() {
  analyticsEnabled = false
  delete window.posthog
  if (!activeClient) return

  try {
    activeClient.stopSessionRecording?.()
  } catch {
    // Keep applying the remaining independent privacy controls.
  }
  try {
    activeClient.set_config?.({
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_persistence: true,
      disable_session_recording: true,
    })
  } catch {
    // Keep applying the remaining independent privacy controls.
  }
  try {
    activeClient.opt_out_capturing?.()
  } catch {
    // A blocked analytics SDK must not interrupt private-page navigation.
  }
}

export function syncPublicAnalyticsForPage(page: AnalyticsPage, client?: AnalyticsClient) {
  const props = page.props as {
    analytics?: PublicAnalyticsConfig | null
    current_user?: unknown
  }
  if (page.component.startsWith("Marketing/") && !props.current_user) {
    return initializePublicAnalytics(props.analytics, client)
  }

  disablePublicAnalytics()
  return false
}

export function captureAnalyticsEvent(event: AnalyticsEventName, properties: AnalyticsProperties) {
  if (!activeClient || !analyticsEnabled) return

  const allowed = EVENT_PROPERTIES[event]
  const safeProperties = Object.fromEntries(
    allowed.flatMap((key) => {
      const value = properties[key]
      return validProperty(key, value) ? [[key, value]] : []
    }),
  )

  try {
    activeClient.capture(event, safeProperties)
  } catch {
    // Analytics is optional and must never interrupt a public journey.
  }
}

export function getAnalyticsIdentity() {
  if (!activeClient || !analyticsEnabled) return null

  try {
    const distinctId = activeClient.get_distinct_id().trim()
    return distinctId.length > 0 && distinctId.length <= 200 ? distinctId : null
  } catch {
    return null
  }
}

export function sanitizeAnalyticsEvent(event: AnalyticsEvent | null): AnalyticsEvent | null {
  if (!event) return null

  const safeEvent = { ...event }
  delete safeEvent.$set
  delete safeEvent.$set_once
  return {
    ...safeEvent,
    properties: sanitizeValue(event.properties) as Record<string, unknown> | undefined,
  }
}

export function stripQueryString(value: string) {
  const isAbsolute = /^https?:\/\//i.test(value)
  const isRelative = value.startsWith("/")
  if (!isAbsolute && !isRelative) return value

  try {
    const url = new URL(value, window.location.origin)
    url.search = ""
    return isAbsolute ? url.toString() : `${url.pathname}${url.hash}`
  } catch {
    return value.split("?")[0]
  }
}

export function resetAnalyticsForTests() {
  activeClient = null
  analyticsEnabled = false
  delete window.posthog
}

function privacyFirstConfig(host: string): Partial<PostHogConfig> {
  return {
    api_host: host,
    autocapture: true,
    capture_pageview: "history_change",
    capture_pageleave: true,
    defaults: "2026-05-30",
    disable_session_recording: false,
    disable_persistence: false,
    disable_surveys: true,
    person_profiles: "always",
    persistence: "localStorage+cookie",
    mask_all_element_attributes: true,
    mask_all_text: true,
    property_denylist: Array.from(FORBIDDEN_PROPERTY_NAMES),
    strict_script_versioning: true,
    before_send: (event) => sanitizeAnalyticsEvent(event as AnalyticsEvent | null) as typeof event,
    session_recording: {
      maskAllInputs: true,
      recordBody: false,
      recordHeaders: false,
      maskCapturedNetworkRequestFn: ({ requestBody: _requestBody, responseBody: _responseBody, requestHeaders: _requestHeaders, responseHeaders: _responseHeaders, ...request }) => ({
        ...request,
        name: request.name ? stripQueryString(request.name) : request.name,
      }),
    },
  }
}

function validConfig(config: PublicAnalyticsConfig | null | undefined): config is PublicAnalyticsConfig {
  if (!config?.key.trim()) return false

  try {
    return new URL(config.host).protocol === "https:"
  } catch {
    return false
  }
}

function validProperty(key: keyof AnalyticsProperties, value: unknown) {
  if (key === "locale") return value === "hr" || value === "en"
  if (key === "lead_kind") return value === "website_review" || value === "consultation"
  if (key === "page_id") return typeof value === "string" && PUBLIC_PAGE_IDS.has(value)

  return false
}

function sanitizeValue(value: unknown, key = ""): unknown {
  const normalizedKey = key.toLowerCase().replace(/^attr__/, "")
  if (FORBIDDEN_PROPERTY_NAMES.has(normalizedKey)) return undefined
  if (typeof value === "string") return stripQueryString(value)
  if (Array.isArray(value)) return value.map((item) => sanitizeValue(item)).filter((item) => item !== undefined)
  if (!value || typeof value !== "object") return value

  return Object.fromEntries(
    Object.entries(value).flatMap(([entryKey, entryValue]) => {
      const sanitized = sanitizeValue(entryValue, entryKey)
      return sanitized === undefined ? [] : [[entryKey, sanitized]]
    }),
  )
}
